// src/pages/settings/Locations.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import { Header, Modal, Field, Actions, asArray } from './SettingsUI';

export default function Locations() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data: list = [], isLoading } = useQuery(['locations'], () =>
    api.get('/api/locations').then((r) => asArray(r.data)));

  const save = useMutation(
    (b) => b._id ? api.put(`/api/locations/${b._id}`, b) : api.post('/api/locations', b),
    { onSuccess: () => { qc.invalidateQueries(['locations']); setEditing(null); }, onError: (e) => alert(e?.response?.data?.message || e.message) });
  const del = useMutation((id) => api.delete(`/api/locations/${id}`),
    { onSuccess: () => qc.invalidateQueries(['locations']), onError: (e) => alert(e?.response?.data?.message || e.message) });

  return (
    <div className="p-5">
      <Header title={t('settings.locations', 'Locations')} onAdd={() => setEditing({})} addLabel={t('common.add', 'Add')} />
      {isLoading ? <div className="text-gray-400 py-8">…</div> : !list.length ? <div className="text-gray-400 py-8">{t('common.none', 'Nothing yet')}</div> : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500"><tr>
              <th className="text-start px-4 py-2">{t('common.name', 'Name')}</th>
              <th className="text-start px-4 py-2">{t('common.phone', 'Phone')}</th>
              <th className="text-start px-4 py-2">{t('common.address', 'Address')}</th>
              <th className="text-start px-4 py-2">{t('common.status', 'Status')}</th>
              <th className="px-4 py-2 w-28"></th>
            </tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r._id} className="border-t">
                  <td className="px-4 py-2 font-semibold">{r.name}{r.is_default && <span className="ms-2 text-xs text-emerald-600">({t('common.default', 'default')})</span>}</td>
                  <td className="px-4 py-2 text-gray-600">{r.phone}</td>
                  <td className="px-4 py-2 text-gray-600">{r.address}</td>
                  <td className="px-4 py-2">{r.status || 'active'}</td>
                  <td className="px-4 py-2 text-end whitespace-nowrap">
                    <button onClick={() => setEditing(r)} className="text-blue-600 font-semibold me-3">{t('common.edit', 'Edit')}</button>
                    {!r.is_default && <button onClick={() => window.confirm(t('common.confirmDelete', 'Delete this item?')) && del.mutate(r._id)} className="text-red-600 font-semibold">{t('common.delete', 'Delete')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <LocForm row={editing} t={t} busy={save.isLoading} onClose={() => setEditing(null)} onSave={(b) => save.mutate(b)} />}
    </div>
  );
}

function LocForm({ row, onClose, onSave, busy, t }) {
  const [f, setF] = useState({ name: row.name || '', name_ar: row.name_ar || '', address: row.address || '', phone: row.phone || '', status: row.status || 'active' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={row._id ? t('common.edit', 'Edit') : t('common.add', 'Add')} onClose={onClose}>
      <div className="space-y-3">
        <Field label={t('common.name', 'Name')}><input value={f.name} onChange={set('name')} className="inp" /></Field>
        <Field label={t('common.nameAr', 'Arabic name')}><input value={f.name_ar} onChange={set('name_ar')} className="inp" dir="rtl" /></Field>
        <Field label={t('common.phone', 'Phone')}><input value={f.phone} onChange={set('phone')} className="inp" /></Field>
        <Field label={t('common.address', 'Address')}><input value={f.address} onChange={set('address')} className="inp" /></Field>
        <Field label={t('common.status', 'Status')}>
          <select value={f.status} onChange={set('status')} className="inp"><option value="active">active</option><option value="inactive">inactive</option></select>
        </Field>
        <Actions busy={busy} disabled={!f.name} onClose={onClose} onSave={() => onSave({ ...row, ...f })}
                 cancelLabel={t('common.cancel', 'Cancel')} saveLabel={t('common.save', 'Save')} savingLabel={t('common.saving', 'Saving…')} />
      </div>
    </Modal>
  );
}